import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Application, PortalApplicationSource } from './models';
import { nextApplicationPage } from './next-application-page';
import { CurrentApplicationService } from './services';

@Component({
    selector: 'app-application',
    templateUrl: './application.component.html',
    styleUrls: ['./application.component.scss'],
    standalone: true,
    imports: [
        TranslocoModule,
        MatIcon,
        CommonModule,
        RouterModule,
        FlexLayoutModule
    ]
})
export class ApplicationComponent implements OnDestroy {
    application: Application | undefined;
    subscription: Subscription | undefined;

    constructor(
        private _currentApplication: CurrentApplicationService,
        private _router: Router
    ) {
        // No reset()/load() here: ApplicationEntryGuard has already loaded the
        // application and written any staged provider selection onto it, so `latest` is
        // populated and authoritative by the time this runs.
        this.subscription = this._currentApplication.latest
            .pipe(
                filter(
                    (application) =>
                        application !== null &&
                        application !== undefined &&
                        application.applicationId != undefined
                )
            )
            .subscribe((application) => {
                this.application = new Application({ ...application });
                this._router.navigateByUrl(this.nextPage(this.application));
            });
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    /**
     * Whether this application began from a provider's deep link.
     *
     * Such a student came in to apply to one specific agency, so the flow does not offer to send
     * them off picking a different one — "Back to Providers" is hidden for them.
     *
     * Read from the application rather than ProviderLinkService or sessionStorage: both describe
     * the current visit and are lost on refresh or a new session, whereas this is a property of
     * the application itself and holds however the student comes back to it.  Applications
     * predating the pathway being recorded have no source and keep the link.
     */
    get startedFromProviderLink(): boolean {
        return (
            this.application?.portalApplicationSource ===
            PortalApplicationSource.agency
        );
    }

    /**
     * Moves the student on as each step is saved.  ApplicationEntryGuard applies the same rule
     * when they first enter the flow — this handles the emissions that follow, when a page
     * reloads the application after saving.
     */
    nextPage(application: Application | undefined) {
        return nextApplicationPage(application);
    }

    navigation(route: string) {
        this._router.navigateByUrl(`${runtimeConfig.clientCode}/${route}`);
    }

    isStepActive(route: string): boolean {
        return this._router.isActive(`/${runtimeConfig.clientCode}/${route}`, {
            paths: 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored'
        });
    }
}
